var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { useGA4React } from "ga-4-react";
import { CAlert, CCard, CCardBody, CCardGroup, CForm, CFormInput, CInputGroup, CInputGroupText, CModal, } from "@coreui/react";
import { cilEnvelopeClosed, cilLockLocked } from "@coreui/icons";
import CIcon from "@coreui/icons-react";
import RouterNavLink from "../../RouterNavLink";
import apiUrl from "../../../constants/apiUrl.constant";
import useAuth from "../../../hooks/useAuth";
import { CenteredButton } from "../../index";
import { publicRequest } from "../../../api/requestManager";
import "./LoginModal.scss";
const LoginModal = ({ closeModal, size, modalVisible }) => {
    const ga = useGA4React();
    const [searchParams, setSearchParams] = useSearchParams();
    const navigate = useNavigate();
    const { pathname } = useLocation();
    const { convertAuthData, setAuth } = useAuth();
    const [email, setEmail] = useState("");
    const [isCardGroupSmall, setIsCardGroupSmall] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [loginErrorMessage, setLoginErrorMessage] = useState("");
    const [password, setPassword] = useState("");
    const [unsuccessfulActivation, setUnsuccessfulActivation] = useState(false);
    const previousShouldChangeCardGroupRef = useRef(false);
    const emailRef = useRef();
    const errRef = useRef();
    const resetModal = useCallback(() => {
        setEmail("");
        setPassword("");
        setLoginErrorMessage("");
        setSearchParams({});
        setUnsuccessfulActivation(false);
    }, [setSearchParams]);
    const resetAndCloseModal = useCallback(() => {
        resetModal();
        closeModal({ afterClose: () => setSearchParams({}) });
    }, [closeModal, resetModal, setSearchParams]);
    const handleRegisterButton = useCallback(() => {
        resetAndCloseModal();
        navigate("/registration");
    }, [resetAndCloseModal, navigate]);
    const handleSubmit = useCallback((email, password) => __awaiter(void 0, void 0, void 0, function* () {
        var _a;
        setUnsuccessfulActivation(false);
        setIsLoading(true);
        try {
            const response = yield publicRequest({
                url: apiUrl.login,
                method: "POST",
                data: { email, password },
            });
            if ((response === null || response === void 0 ? void 0 : response.data) && response.status === 200) {
                setAuth(convertAuthData(response.data));
                setIsLoading(false);
                if (ga) {
                    ga.gtag("event", "login", {
                        action: "sikeres_login",
                    });
                }
                if (pathname.includes("search") && searchParams.get("favorites") === "true") {
                    closeModal();
                    return;
                }
                resetAndCloseModal();
                navigate("/");
                return;
            }
            else if ((response === null || response === void 0 ? void 0 : response.status) === 400) {
                setLoginErrorMessage("Az email cím vagy a jelszó nem megfelelő.");
            }
            else if ((response === null || response === void 0 ? void 0 : response.status) === 401) {
                setLoginErrorMessage("Bejelentkezés sikertelen. Hibás email cím vagy jelszó, vagy a fiók nem lett aktiválva.");
            }
            else {
                setLoginErrorMessage("Bejelentkezés sikertelen.");
            }
            setIsLoading(false);
        }
        catch (err) {
            setLoginErrorMessage("Bejelentkezés sikertelen.");
            setIsLoading(false);
            (_a = errRef === null || errRef === void 0 ? void 0 : errRef.current) === null || _a === void 0 ? void 0 : _a.focus();
        }
    }), [
        closeModal,
        convertAuthData,
        ga,
        navigate,
        pathname,
        resetAndCloseModal,
        searchParams,
        setAuth,
    ]);
    const handleTyping = (event) => {
        var _a, _b;
        if (event.key === "Enter") {
            const email = (_a = document.querySelector("#email")) === null || _a === void 0 ? void 0 : _a.nodeValue;
            const password = (_b = document.querySelector("#password")) === null || _b === void 0 ? void 0 : _b.nodeValue;
            if (email && password) {
                handleSubmit(email, password);
                return;
            }
        }
        if (event.key === "Escape") {
            resetModal();
            closeModal();
            return;
        }
    };
    useEffect(() => {
        var _a;
        (_a = emailRef === null || emailRef === void 0 ? void 0 : emailRef.current) === null || _a === void 0 ? void 0 : _a.focus();
        document.addEventListener("keydown", handleTyping);
        return () => document.removeEventListener("keydown", handleTyping);
        // eslint-disable-next-line
    }, []);
    /*
    useEffect(() => {
      setLoginErrorMessage("");
      if (email || password) {
        setSearchParams({});
        setSuccessfulActivation(false);
        setUnsuccessfulActivation(false);
      }
    }, [email, password, setSearchParams]);
    */
    useEffect(() => {
        for (const [key, value] of searchParams) {
            if (key === "login") {
                if (value === "false") {
                    setUnsuccessfulActivation(true);
                    return;
                }
            }
        }
    }, [searchParams]);
    useEffect(() => {
        const measureWindowForCardGroup = () => {
            const shouldChangeCardGroup = window.innerWidth <= 578;
            if (shouldChangeCardGroup !== previousShouldChangeCardGroupRef.current) {
                previousShouldChangeCardGroupRef.current = shouldChangeCardGroup;
                setIsCardGroupSmall(shouldChangeCardGroup);
            }
        };
        window.addEventListener("resize", measureWindowForCardGroup);
        measureWindowForCardGroup();
        return () => window.removeEventListener("resize", measureWindowForCardGroup);
    }, []);
    return (_jsxs(CModal, Object.assign({ backdrop: "static", visible: modalVisible, size: size, onClose: resetAndCloseModal }, { children: [_jsx("button", { type: "button", className: `btn btn-close ${isCardGroupSmall ? "" : "btn-close-white"}`, "aria-label": "Close", style: {
                    display: "block",
                    position: "absolute",
                    zIndex: 1,
                    top: "16px",
                    right: "16px",
                }, onClick: resetAndCloseModal }), _jsxs(CCardGroup, { children: [_jsx(CCard, Object.assign({ className: "p-4" }, { children: _jsx(CCardBody, { children: _jsxs(CForm, { children: [unsuccessfulActivation && (_jsx(CAlert, Object.assign({ color: "danger" }, { children: "Az aktiv\u00E1ci\u00F3s link nem \u00E9rv\u00E9nyes!" }))), _jsx("h2", Object.assign({ className: "text-nowrap" }, { children: "Bejelentkez\u00E9s" })), loginErrorMessage && _jsx(CAlert, Object.assign({ color: "danger" }, { children: loginErrorMessage })), _jsx("p", Object.assign({ className: "text-medium-emphasis" }, { children: "L\u00E9pj be fi\u00F3kodba az email c\u00EDmeddel \u00E9s a jelszavaddal!" })), _jsxs(CInputGroup, Object.assign({ className: "mb-3" }, { children: [_jsx(CInputGroupText, { children: _jsx(CIcon, { icon: cilEnvelopeClosed }) }), _jsx(CFormInput, { autoComplete: "off", id: "email", name: "email", onChange: (e) => setEmail(e.target.value), placeholder: "Email", ref: emailRef, required: true, value: email })] })), _jsxs(CInputGroup, Object.assign({ className: "mb-4" }, { children: [_jsx(CInputGroupText, { children: _jsx(CIcon, { icon: cilLockLocked }) }), _jsx(CFormInput, { autoComplete: "on", id: "password", onChange: (e) => setPassword(e.target.value), placeholder: "Jelsz\u00F3", required: true, type: "password", value: password })] })), _jsxs("div", Object.assign({ className: "d-flex flex-row flex-wrap justify-content-between align-items-center" }, { children: [_jsx("div", { children: _jsx(CenteredButton, { disabled: isLoading, loading: isLoading, onClick: () => handleSubmit(email, password), text: "Bejelentkez\u00E9s" }) }), _jsx("div", Object.assign({ className: "text-right text-wrap" }, { children: _jsx(RouterNavLink, Object.assign({ to: "/forgotten-password", onClick: resetAndCloseModal }, { children: "Elfelejtetted a jelsz\u00F3t?" })) }))] }))] }) }) })), _jsx(CCard, Object.assign({ className: "text-white bg-primary py-5" }, { children: _jsx(CCardBody, Object.assign({ className: "text-center" }, { children: _jsxs("div", { children: [_jsx("h2", { children: "Regisztr\u00E1ci\u00F3" }), _jsx("p", { children: "A regisztr\u00E1l\u00F3k elmenthetik kedvezm\u00E9nyekkel, akci\u00F3kkal kapcsolatos be\u00E1ll\u00EDt\u00E1saikat \u00E9s feliratkozhatnak szem\u00E9lyre szabott h\u00EDrlevel\u00FCnkre." }), _jsx(CenteredButton, { className: "mt-3", active: true, onClick: handleRegisterButton, tabIndex: -1, text: "Regisztr\u00E1lj most!" })] }) })) }))] })] })));
};
export default LoginModal;
